import type { Application } from '@/types';

import { isBulkTargetPendingHydration } from '@/lib/bulkProcessQueueSync';

/** Fast poll while enqueue/list lag; slower when every row is PIPELINE_RUNNING. */
export const BULK_QUEUE_POLL_MIN_MS = 4_000;
export const BULK_QUEUE_POLL_MAX_MS = 10_000;

/** Throttle full company list refetch during bulk — bulk-status covers in-flight rows. */
export const BULK_LIST_REFETCH_MIN_MS = 12_000;

const listRefetchAtByCompany = new Map<string, number>();

export function shouldRefetchApplicationsListDuringBulk(companyId: string): boolean {
  const now = Date.now();
  const last = listRefetchAtByCompany.get(companyId) ?? 0;
  if (now - last < BULK_LIST_REFETCH_MIN_MS) {
    return false;
  }
  listRefetchAtByCompany.set(companyId, now);
  return true;
}

export function resetBulkListRefetchThrottle(companyId: string): void {
  listRefetchAtByCompany.delete(companyId);
}

/**
 * Adaptive interval for bulk queue polling loops.
 * All targets still in PIPELINE_RUNNING → slower polls (pipelines take minutes).
 */
export function getBulkQueuePollIntervalMs(
  apps: readonly Application[],
  pendingIds: readonly string[],
): number {
  if (pendingIds.length === 0) {
    return BULK_QUEUE_POLL_MAX_MS;
  }

  const pendingRows = pendingIds
    .map((id) => apps.find((a) => a.id === id))
    .filter((row): row is Application => row != null)
    .filter((row) => isBulkTargetPendingHydration(row));

  if (pendingRows.length === 0) {
    return BULK_QUEUE_POLL_MIN_MS;
  }

  const allPipelineRunning = pendingRows.every((row) => row.state === 'PIPELINE_RUNNING');
  return allPipelineRunning ? BULK_QUEUE_POLL_MAX_MS : BULK_QUEUE_POLL_MIN_MS;
}
